import { html, LitElement, nothing, TemplateResult } from 'lit'
import { customElement, property, query } from 'lit/decorators.js'
import { CSESlideIn } from '@cloudsuite/mosaic-elements'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { initializeIconsByRole } from '@cloudsuite/mosaic-shared/icons/library.js'
import { consume } from '@lit/context'
import {
  listQuotationsContext,
  quotationsDataContext,
  quotationSummaryContext,
  summarizeQuotationsContext
} from 'components/ordering/quotation/components/context/quotation-context.js'
import { QuotationSummaryDTO } from 'components/ordering/quotation/api/dtos/quotation-summary-dto.js'
import { map } from 'lit/directives/map.js'
import { CSCQuotationStatus } from 'components/ordering/quotation/components/status.js'
import { QuotationStatus } from 'components/ordering/quotation/api/dtos/quotation-status.js'
import { QuotationDTO } from 'components/ordering/quotation/api/dtos/quotation-dto.js'
import { GetQuotationsPayload, ListQuotations } from 'components/ordering/quotation/api/requests/list-quotations.js'
import { executeQuotationRequest } from 'components/ordering/quotation/api/executor.js'

initializeIconsByRole({
  'csc-quotation-overview-filter-slide-in.show_results': { name: 'filter', style: 'light' },
})

const translations = {
  header: () => t('header', {
    ns: 'csc-quotation-overview-filter-slide-in',
    default: 'Filter quotations',
  }),
  noFilterOptionsAvailable: () => t('no_filter_options_available', {
    ns: 'csc-quotation-overview-filter-slide-in',
    default: 'No filter options available',
  }),
  userLabel: () => t('user_label', {
    ns: 'csc-quotation-overview-filter-slide-in',
    default: 'User',
  }),
  userPlaceholder: () => t('user_placeholder', {
    ns: 'csc-quotation-overview-filter-slide-in',
    default: 'Select a user',
  }),
  statusLabel: () => t('status_label', {
    ns: 'csc-quotation-overview-filter-slide-in',
    default: 'Status',
  }),
  statusPlaceholder: () => t('status_placeholder', {
    ns: 'csc-quotation-overview-filter-slide-in',
    default: 'Select a status',
  }),
  infoMessageMaximumResults: () => t('info_message_maximum_results', {
    ns: 'csc-quotation-overview-filter-slide-in',
    default: 'Maximum of 100 results',
  }),
  showResults: () => t('show_results', {
    ns: 'csc-quotation-overview-filter-slide-in',
    default: 'Show results',
  })
}

@customElement('csc-quotation-overview-filter-slide-in')
export class CSCQuotationOverviewFilterSlideIn extends LitElement {
  @property({ type: String, attribute: 'target-id' })
  targetId: string = 'overview-filter'

  @query('cse-slide-in')
  private _slideIn!: CSESlideIn | null | undefined

  @consume({ context: quotationsDataContext })
  public quotationsData: QuotationDTO[] = []


  @consume({ context: listQuotationsContext })
  public listQuotations = async (filters: GetQuotationsPayload = {}) => {
    const request = new ListQuotations(filters)
    this.quotationsData = await executeQuotationRequest(request)
  }

  @consume({ context: summarizeQuotationsContext })
  private summarizeQuotations!: () => Promise<void>

  @consume({ context: quotationSummaryContext, subscribe: true })
  private quotationsSummary!: QuotationSummaryDTO

  private firstFetchDone = false

  get noQuotationSummary() {
    return this.firstFetchDone && this.quotationsSummary !== null
  }

  private get users() {
    if (this.noQuotationSummary) {
      return []
    }

    return this.quotationsSummary.users || []
  }

  private get statuses() {
    if (this.noQuotationSummary) {
      return []
    }

    return this.quotationsSummary.statuses || []
  }

  private renderStatusOption(status: QuotationStatus) {
    const statusComponent = new CSCQuotationStatus()
    statusComponent.status = status
    const translation = statusComponent.translation
    return html`
      <cse-option value="${status}">
        ${translation}
      </cse-option>`
  }

  private renderUserOption(user: { emailAddress: string; userId: number }) {
    return html`
      <cse-option value="${user.userId}">
        ${user.emailAddress}
      </cse-option>`
  }

  open() {
    if (this._slideIn) {
      this._slideIn.open().then(() => nothing)
    }
  }

  private filterUser: number | null = null

  private filterStatus: QuotationStatus | null = null

  private filterUserChanged = async (event: CustomEvent) => {
    const select = event.target as any

    this.filterUser = select.value ? parseInt(select.value) : null
    await this.filterResults()
  }

  private filterStatusChanged = async (event: CustomEvent) => {
    const select = event.target as any

    this.filterStatus = select.value ? select.value as QuotationStatus : null
    await this.filterResults()
  }

  private async filterResults() {
    const filters: GetQuotationsPayload = {}
    if (this.filterUser !== null) {
      filters.userId = this.filterUser
    }
    if (this.filterStatus !== null) {
      filters.status = this.filterStatus
    }
    await this.listQuotations(filters)
  }


  override async connectedCallback() {
    super.connectedCallback()

    await this.summarizeQuotations()
    this.firstFetchDone = true
  }

  override render(): TemplateResult {
    return html`
      <cse-slide-in size="default" id="${this.targetId}" placement="right">
        <div slot="header">
          ${translations.header()}
        </div>

        ${this.users.length === 0 && this.statuses.length === 0 ? html`
          <cse-alert variant="error">
            ${translations.noFilterOptionsAvailable()}
          </cse-alert>
        ` : nothing}

        <cse-fieldset>
          ${this.users.length > 0 ? html`
            <cse-form-field>
              <cse-label slot="label">
                ${translations.userLabel()}
              </cse-label>

              <cse-select placeholder="${translations.userPlaceholder()}" @change="${this.filterUserChanged}">
                ${map(this.users, (user) => this.renderUserOption(user))}
              </cse-select>
            </cse-form-field>
          ` : nothing}

          ${this.statuses.length > 0 ? html`
            <cse-form-field>
              <cse-label slot="label">
                ${translations.statusLabel()}
              </cse-label>

              <cse-select placeholder="${translations.statusPlaceholder()}" @change="${this.filterStatusChanged}">
                ${map(this.statuses, (status) => this.renderStatusOption(status))}
              </cse-select>
            </cse-form-field>
          ` : nothing}
        </cse-fieldset>

        <cse-alert variant="info">
          ${translations.infoMessageMaximumResults()}
        </cse-alert>

        <cse-button slot="footer" fit="fill" variant="primary" @click="${() => this._slideIn?.close()}">
          <cse-icon icon-role="csc-quotation-overview-filter-slide-in.show_results"></cse-icon>

          ${translations.showResults()}
        </cse-button>
      </cse-slide-in>
    `
  }
}
